import { useState, useEffect, useCallback, useRef } from 'react';
import { EntitiesService } from '@/services/entities.service';
import type { EntitySummary, SyncStatus, SyncJobStatus } from '@/types/entity.types';

interface SyncStatusData {
  entityId: string;
  syncStatus: SyncStatus | null;
  lastSync?: string;
  connectionStatus?: string;
  // Enhanced sync job tracking
  activeSyncJobId?: string;
  syncJobStatus?: SyncJobStatus;
  // Metadata for cleanup
  lastUpdated: number; // timestamp for garbage collection
  requestCount: number; // track API request frequency
}

interface UseSyncStatusPollingOptions {
  pollingInterval?: number; // in milliseconds, default 30000 (30 seconds) for idle, 5000 (5 seconds) for active
  enabled?: boolean; // allows pausing polling
  useJobBasedPolling?: boolean; // use enhanced job-based polling when available
  maxMapSize?: number; // maximum number of entities to track, default 50
  cleanupInterval?: number; // interval to run garbage collection, default 300000 (5 minutes)
  completedSyncRetention?: number; // how long to keep completed syncs in ms, default 60000 (1 minute)
}

interface UseSyncStatusPollingReturn {
  syncStatuses: Map<string, SyncStatusData>;
  isPolling: boolean;
  error: string | null;
  refreshSyncStatus: (entityId?: string) => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
  registerSyncJob: (entityId: string, syncJobId: string) => void;
}

export function useSyncStatusPolling(
  entityIds: string[] = [],
  options: UseSyncStatusPollingOptions = {}
): UseSyncStatusPollingReturn {
  const {
    pollingInterval = 30000,
    enabled = true,
    useJobBasedPolling = true,
    maxMapSize = 50,
    cleanupInterval = 300000, // 5 minutes
    completedSyncRetention = 60000 // 1 minute
  } = options;
  
  const [syncStatuses, setSyncStatuses] = useState<Map<string, SyncStatusData>>(new Map());
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const activeSyncJobs = useRef<Map<string, string>>(new Map()); // entityId -> syncJobId
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestCache = useRef<Map<string, Promise<SyncStatusData | null>>>(new Map()); // request deduplication cache

  // Garbage collection for completed syncs and stale data
  const runGarbageCollection = useCallback(() => {
    const now = Date.now();
    const entriesToRemove: string[] = [];

    // Check current syncStatuses Map for cleanup opportunities
    syncStatuses.forEach((statusData, entityId) => {
      // Remove entities that are no longer in entityIds array
      if (!entityIds.includes(entityId)) {
        entriesToRemove.push(entityId);
        return;
      }

      // Remove completed syncs after retention period
      if (statusData.syncJobStatus?.status === 'completed' || statusData.syncJobStatus?.status === 'failed') {
        if (now - statusData.lastUpdated > completedSyncRetention) {
          entriesToRemove.push(entityId);
          activeSyncJobs.current.delete(entityId);
        }
      }

      // Remove stale data that hasn't been updated in a long time (10 minutes)
      if (now - statusData.lastUpdated > 600000) {
        entriesToRemove.push(entityId);
        activeSyncJobs.current.delete(entityId);
      }
    });

    // Enforce maximum map size
    if (syncStatuses.size > maxMapSize) {
      const sortedEntries = Array.from(syncStatuses.entries())
        .sort((a, b) => a[1].lastUpdated - b[1].lastUpdated); // oldest first

      const excessCount = syncStatuses.size - maxMapSize;
      for (let i = 0; i < excessCount; i++) {
        entriesToRemove.push(sortedEntries[i][0]);
      }
    }

    // Apply cleanup if needed
    if (entriesToRemove.length > 0) {
      setSyncStatuses(prev => {
        const newMap = new Map(prev);
        entriesToRemove.forEach(entityId => {
          newMap.delete(entityId);
          activeSyncJobs.current.delete(entityId);
        });
        return newMap;
      });

      console.debug(`Sync status garbage collection: removed ${entriesToRemove.length} entries`);
    }

    // Clear request cache periodically
    requestCache.current.clear();
  }, [entityIds, syncStatuses, maxMapSize, completedSyncRetention]);

  const fetchJobBasedSyncStatus = useCallback(async (entityId: string, syncJobId: string): Promise<SyncStatusData | null> => {
    try {
      const jobStatus = await EntitiesService.getSyncJobStatus(syncJobId);

      // If job is completed or failed, remove from active jobs
      if (jobStatus.status === 'completed' || jobStatus.status === 'failed') {
        activeSyncJobs.current.delete(entityId);
      }

      return {
        entityId,
        syncStatus: null, // We rely on syncJobStatus for job-based polling
        activeSyncJobId: syncJobId,
        syncJobStatus: jobStatus,
        lastUpdated: Date.now(),
        requestCount: (syncStatuses.get(entityId)?.requestCount || 0) + 1
      };
    } catch (err) {
      console.error(`Error fetching sync job status for ${syncJobId}:`, err);
      // If job status fails, fall back to entity-based polling
      activeSyncJobs.current.delete(entityId);
      return null;
    }
  }, [syncStatuses]);

  const fetchSyncStatus = useCallback(async (entityId: string): Promise<SyncStatusData | null> => {
    // Request deduplication - check if same request is already in flight
    const cacheKey = `${entityId}-${useJobBasedPolling}`;
    const existingRequest = requestCache.current.get(cacheKey);
    if (existingRequest) {
      console.debug(`Deduplicating sync status request for entity ${entityId}`);
      return existingRequest;
    }

    // Create new request and cache it
    const requestPromise = (async (): Promise<SyncStatusData | null> => {
      try {
        // If job-based polling is enabled and we have an active sync job, use that
        const activeSyncJobId = activeSyncJobs.current.get(entityId);
        if (useJobBasedPolling && activeSyncJobId) {
          return await fetchJobBasedSyncStatus(entityId, activeSyncJobId);
        }

        // Fallback to entity-based polling
        let syncStatus: SyncStatus | null = null;
        try {
          const response = await EntitiesService.getSyncStatus(entityId);

          // Check if response is actually sync status or an error
          if (response && typeof response === 'object' && 'detail' in response) {
            // This is likely an auth error, not sync status data
            syncStatus = null;
          } else {
            syncStatus = response;
          }
        } catch (syncErr: any) {
          // If sync status endpoint fails, we'll rely on entity details
          console.warn(`Sync status endpoint error for entity ${entityId}:`, syncErr?.response?.status);
        }

        const entity = await EntitiesService.getEntity(entityId);

        // Update active sync jobs if entity has one
        if (entity.activeSyncJobId && useJobBasedPolling) {
          activeSyncJobs.current.set(entityId, entity.activeSyncJobId);
        }

        return {
          entityId,
          syncStatus,
          lastSync: entity.last_sync,
          connectionStatus: entity.connection_details?.status || 'unknown',
          activeSyncJobId: entity.activeSyncJobId,
          syncJobStatus: entity.syncJobStatus,
          lastUpdated: Date.now(),
          requestCount: (syncStatuses.get(entityId)?.requestCount || 0) + 1
        };
      } catch (err) {
        console.error(`Error fetching entity details for ${entityId}:`, err);
        return null;
      } finally {
        // Remove from cache when request completes
        requestCache.current.delete(cacheKey);
      }
    })();

    // Cache the request
    requestCache.current.set(cacheKey, requestPromise);
    return requestPromise;
  }, [useJobBasedPolling, fetchJobBasedSyncStatus, syncStatuses]);

  const refreshSyncStatus = useCallback(async (specificEntityId?: string) => {
    const entitiesToUpdate = specificEntityId ? [specificEntityId] : entityIds;

    if (entitiesToUpdate.length === 0) return;

    // Create per-request AbortController to avoid race conditions
    const requestAbortController = new AbortController();

    // Cancel previous global request if still running
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Store the current request controller
    abortControllerRef.current = requestAbortController;

    try {
      setError(null);

      // Use Promise.allSettled to handle individual failures gracefully
      const statusPromises = entitiesToUpdate.map(async entityId => {
        // Check if request was cancelled before starting
        if (requestAbortController.signal.aborted) {
          return Promise.reject(new Error('Request cancelled'));
        }

        try {
          const result = await fetchSyncStatus(entityId);

          // Check again if request was cancelled after completion
          if (requestAbortController.signal.aborted) {
            return Promise.reject(new Error('Request cancelled'));
          }

          return result;
        } catch (error) {
          // Log individual entity errors for debugging
          console.warn(`Failed to fetch sync status for entity ${entityId}:`, error);
          throw error;
        }
      });

      const statusResults = await Promise.allSettled(statusPromises);

      // Final check if request was cancelled
      if (requestAbortController.signal.aborted) {
        console.debug('Sync status refresh was cancelled');
        return;
      }

      setSyncStatuses(prev => {
        const newMap = new Map(prev);
        let updatedCount = 0;

        statusResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            const entityId = entitiesToUpdate[index];
            newMap.set(result.value.entityId, result.value);
            updatedCount++;
          } else if (result.status === 'rejected') {
            const entityId = entitiesToUpdate[index];
            console.warn(`Failed to fetch sync status for entity ${entityId}:`, result.reason);
          }
        });

        console.debug(`Sync status refresh completed: ${updatedCount}/${entitiesToUpdate.length} entities updated`);
        return newMap;
      });

      // Run garbage collection after successful update
      runGarbageCollection();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch sync statuses';
      console.warn('Error refreshing sync statuses (non-critical):', errorMessage);

      // Enhanced error handling with retry logic for critical errors
      if (err instanceof Error && err.message.includes('Network Error')) {
        console.debug('Network error detected, will retry on next polling cycle');
      }
    } finally {
      // Clear the abort controller reference if it's the current one
      if (abortControllerRef.current === requestAbortController) {
        abortControllerRef.current = null;
      }
    }
  }, [entityIds, fetchSyncStatus, runGarbageCollection]);

  // Method to register a sync job for job-based polling
  const registerSyncJob = useCallback((entityId: string, syncJobId: string) => {
    activeSyncJobs.current.set(entityId, syncJobId);
    // Trigger immediate refresh for this entity
    refreshSyncStatus(entityId);
  }, [refreshSyncStatus]);

  const startPolling = useCallback(() => {
    if (isPollingRef.current || !enabled || entityIds.length === 0) return;

    isPollingRef.current = true;
    setIsPolling(true);

    // Initial fetch
    refreshSyncStatus();

    // Set up dynamic interval based on active sync jobs
    const pollWithDynamicInterval = () => {
      if (!isPollingRef.current) return;

      refreshSyncStatus();

      // Determine next interval based on active sync jobs
      const hasActiveSyncs = activeSyncJobs.current.size > 0;
      const nextInterval = hasActiveSyncs
        ? 5000 // 5 seconds for active syncs (90% reduction in API calls vs entity polling)
        : pollingInterval; // 30 seconds for idle state

      intervalRef.current = setTimeout(pollWithDynamicInterval, nextInterval);
    };

    // Start the dynamic polling
    intervalRef.current = setTimeout(pollWithDynamicInterval, pollingInterval);
  }, [enabled, entityIds.length, pollingInterval, refreshSyncStatus]);

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    setIsPolling(false);

    // Cancel any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clear request deduplication cache
    requestCache.current.clear();

    // Clear timeout (not interval anymore)
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }

    // Clear cleanup timer
    if (cleanupIntervalRef.current) {
      clearInterval(cleanupIntervalRef.current);
      cleanupIntervalRef.current = null;
    }
  }, []);

  // Start/stop polling based on entityIds and enabled state
  useEffect(() => {
    if (enabled && entityIds.length > 0) {
      // Add a small delay to avoid immediate polling after page load
      const timer = setTimeout(() => {
        startPolling();
      }, 1000);

      // Set up garbage collection interval
      cleanupIntervalRef.current = setInterval(() => {
        runGarbageCollection();
      }, cleanupInterval);

      return () => {
        clearTimeout(timer);
        if (cleanupIntervalRef.current) {
          clearInterval(cleanupIntervalRef.current);
          cleanupIntervalRef.current = null;
        }
        stopPolling();
      };
    } else {
      stopPolling();
    }
  }, [entityIds, enabled, startPolling, stopPolling, runGarbageCollection, cleanupInterval]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    syncStatuses,
    isPolling,
    error,
    refreshSyncStatus,
    startPolling,
    stopPolling,
    registerSyncJob
  };
}

// Convenience hook for single entity
export function useSingleEntitySyncStatus(
  entityId: string | null,
  options: UseSyncStatusPollingOptions = {}
): UseSyncStatusPollingReturn & {
  syncStatus: SyncStatusData | null;
} {
  const entityIds = entityId ? [entityId] : [];
  const result = useSyncStatusPolling(entityIds, options);
  
  const syncStatus = entityId ? result.syncStatuses.get(entityId) || null : null;
  
  return {
    ...result,
    syncStatus
  };
}